import './defines';
import PageObject from 'classes/pageobject';
import TooltipComponent from 'components/tooltip';
import {
	EditorInteractiveInteractionPageObjectModel,
	EditorInteractiveInteractionTemplatePhotoModel,
	EditorInteractiveInteractionTemplateTextModel,
	EditorInteractivePageObjectModel,
	EditorInteractivePageObjectModels,
	EditorInteractivePageObjectResizeSide,
	EditorModulePageTemplatePositions,
	EditorTextOptionsActiveMode,
	FontModel,
	PageObjectModelChangeResult,
	PageObjectVars,
} from 'interfaces/app';
import { OfferingModel } from 'interfaces/database';
import { PageModel } from 'interfaces/project';
import {
	FontModule,
	ProductStateModule,
} from 'store';
import resizeObjectText from 'store/modules/productstate/helpers/resize-text';
import { mobile as mobileTools } from 'tools';
import filterText from 'tools/filter-text';
import {
	dom as domUtils,
	object as objectUtils,
} from 'utils';
import { doubleTapInterval } from 'utils/element-double-tap';
import EditorTextOptionsView from 'views/editor-text-options';
import {
	Component,
	Model,
	Prop,
	Ref,
	Vue,
	Watch,
} from 'vue-property-decorator';
import Template from './template.vue';

@Component({
	name: 'EditorInteractiveView',
	components: {
		EditorTextOptionsView,
	},
})
export default class EditorInteractiveView extends Vue.extend(Template) {
	@Model(
		'change',
		{
			required: true,
			schema: 'EditorInteractivePageObjectModels',
			type: Array,
		},
	)
	public readonly pageObjects!: EditorInteractivePageObjectModels;

	@Prop({
		default: 0,
		type: Number,
	})
	public readonly bleedMargin!: number;

	@Prop({
		description: 'Defines the canvas element that is going to be passed to the editor-text-options-view component',
		required: true,
		type: [HTMLCanvasElement, Function],
	})
	public readonly canvas!: HTMLCanvasElement | (() => HTMLCanvasElement);

	@Prop({
		required: true,
		type: Number,
	})
	public readonly height!: number;

	@Prop({
		default: false,
		description: 'Indicates if the eye dropper is shown, in which case the pointer events are disabled',
		event: 'eye-dropper-shown-change',
		type: Boolean,
	})
	public readonly isEyeDropperShown?: boolean;

	@Prop({
		description: 'Indicates if the photo object is transformable (resizable and rotatable)',
		required: true,
		type: Boolean,
	})
	public readonly isPhotoObjectTransformable!: boolean;

	@Prop({
		description: 'Indicates if the active product supports multiple texts in the page',
		required: true,
		type: Boolean,
	})
	public readonly multipleTextSupport!: boolean;

	@Prop({
		required: true,
		schema: 'OfferingModel',
		type: Object,
	})
	public readonly offeringModel!: OfferingModel;

	@Prop({
		required: true,
		schema: 'PageModel',
		type: Object,
	})
	public readonly pageModel!: PageModel;

	@Prop({
		default: () => ([]),
		schema: 'EditorModulePageTemplatePositions',
		type: Array,
	})
	public readonly pageTemplatePositionsAvailable!: EditorModulePageTemplatePositions;

	@Prop({
		description: 'Defines the type of product the user is working on',
		default: 'photo',
		type: String,
	})
	public readonly productType?: string;

	@Prop({
		required: true,
		type: Number,
	})
	public readonly scaling!: number;

	@Prop({
		default: undefined,
		description: 'Defines the border color of the selected object button',
		type: String,
	})
	public readonly selectedObjectBorderColor?: string;

	@Prop({
		default: undefined,
		description: 'Defines the text and icon color of the add photo button',
		type: String,
	})
	public readonly textColor?: string;

	@Prop({
		default: '#webapp',
		type: [HTMLElement, String],
	})
	public readonly textOptionsAnchor!: HTMLElement | string;

	@Prop({
		required: true,
		type: Number,
	})
	public readonly width!: number;

	private get computedTextOptionsAnchor(): HTMLElement | null {
		if (typeof this.textOptionsAnchor === 'string') {
			return document.querySelector(this.textOptionsAnchor);
		}
		if (this.textOptionsAnchor) {
			return this.textOptionsAnchor;
		}

		return document.getElementById('webapp');
	}

	protected get computedCanvas(): HTMLCanvasElement {
		if (typeof this.canvas === 'function') {
			return this.canvas();
		}

		return this.canvas;
	}

	protected get computedClasses(): Record<string, boolean> {
		return {
			'editor-interactive-view-eye-dropper-shown': this.internalIsEyeDropperShown,
			'editor-interactive-view-text-object-selected': !!this.selectedTextObjectModel,
		};
	}

	protected get computedStyles(): Partial<CSSStyleDeclaration> & Record<string, string> {
		const styles: Partial<CSSStyleDeclaration> & Record<string, string> = {};
		const height = this.height + (this.bleedMargin * 2);
		const width = this.width + (this.bleedMargin * 2);
		styles.height = `${Math.round(height * this.scaling)}px`;
		styles.width = `${Math.round(width * this.scaling)}px`;

		if (this.textColor) {
			styles['--editor-interactive-view-add-photo-text-color'] = this.textColor;
		}

		return styles;
	}

	private get fontModels(): FontModel[] {
		return FontModule.getPrintable;
	}

	protected get interactionObjectModels(): EditorInteractiveInteractionPageObjectModel[] {
		return this.interactionPhotoObjectModels.concat(this.interactionTextObjectModels);
	}

	protected get interactionObjectModelClasses(): (objectModel: EditorInteractivePageObjectModel) => Record<string, boolean> {
		return (objectModel: EditorInteractivePageObjectModel): Record<string, boolean> => ({
			'editor-interactive-view-edit-object-hover': this.isObjectHovered(objectModel),
			'editor-interactive-view-edit-object-photo': objectModel.type === 'photo',
			'editor-interactive-view-edit-object-selected': this.isObjectSelected(objectModel),
			'editor-interactive-view-edit-object-selected-for-edition': this.isObjectSelectedForEdition(objectModel),
			'editor-interactive-view-edit-object-text': objectModel.type === 'text',
			'editor-interactive-view-edit-object-text-movable': (
				objectModel.type === 'text'
				&& !objectModel._selected
			),
		});
	}

	protected get interactionObjectModelStyles(): (interactionPageObjectModel: EditorInteractiveInteractionPageObjectModel) => Partial<CSSStyleDeclaration> & Record<string, string> {
		return ({ object }): Partial<CSSStyleDeclaration> & Record<string, string> => {
			const styles: Partial<CSSStyleDeclaration> & Record<string, string> = {
				height: `${Math.round(object.height * this.scaling)}px`,
				marginLeft: `${Math.round((this.bleedMargin + object.x_axis) * this.scaling)}px`,
				marginTop: `${Math.round((this.bleedMargin + object.y_axis) * this.scaling)}px`,
				transform: `rotate(${object.rotate}deg)`,
			};

			if (!object.pointsize) {
				styles.width = `${Math.round(object.width * this.scaling)}px`;
			} else {
				/**
				 * It may happen that the text object input is not wide enough to
				 * contain the text causing the caret to be misplaced.
				 * To avoid this, we add 20% of the font size to the width.
				 */
				styles.width = `${Math.round((object.width + ((object.pointsize * 31496) / 15120) * 0.2) * this.scaling)}px`;
			}

			if (this.selectedObjectBorderColor) {
				styles['--editor-interactive-view-edit-object-border-color'] = this.selectedObjectBorderColor;
			}
			if (this.internalIsEyeDropperShown) {
				styles.pointerEvents = 'none';
			}

			return styles;
		};
	}

	protected get interactionPhotoObjectModels(): EditorInteractiveInteractionPageObjectModel[] {
		return this.internalPageObjects
			.filter((pageObject) => (
				pageObject.type === 'photo'
				&& pageObject.editable
			))
			.sort((a, b) => a.z_axis - b.z_axis)
			.map((pageObject) => ({
				objectVars: PageObject.calculatePosition(
					{
						x_axis: pageObject.x_axis,
						y_axis: pageObject.y_axis,
						width: pageObject.width,
						height: pageObject.height,
						borderwidth: pageObject.borderwidth,
						bordercolor: pageObject.bordercolor,
						cropx: pageObject.cropx,
						cropy: pageObject.cropy,
						cropwidth: pageObject.cropwidth,
						cropheight: pageObject.cropheight,
						angle: pageObject.rotate,
						flop: Boolean(pageObject.flop),
						flip: Boolean(pageObject.flip),
						type: pageObject.type,
						maxwidth: pageObject.maxwidth,
						photoid: pageObject.photoid || undefined,
					},
					this.scaling,
					null,
					this.bleedMargin,
				),
				object: pageObject,
			}));
	}

	protected get interactionTemplatePhotoTextModels(): (EditorInteractiveInteractionTemplatePhotoModel | EditorInteractiveInteractionTemplateTextModel)[] {
		return this.pageTemplatePositionsAvailable
			.reduce(
				(interactionTemplatePhotoTextModels, pageTemplatePosition) => {
					interactionTemplatePhotoTextModels.push({
						objectVars: PageObject.calculatePosition(
							{
								x_axis: pageTemplatePosition.x,
								y_axis: pageTemplatePosition.y,
								width: pageTemplatePosition.width,
								height: pageTemplatePosition.height,
								borderwidth: pageTemplatePosition.borderwidth,
								bordercolor: pageTemplatePosition.bordercolor,
								angle: pageTemplatePosition.angle,
								type: pageTemplatePosition.type,
							},
							this.scaling,
							null,
							this.bleedMargin,
						),
						templatePosition: pageTemplatePosition as EditorInteractiveInteractionTemplatePhotoModel['templatePosition'] & EditorInteractiveInteractionTemplateTextModel['templatePosition'],
					});

					return interactionTemplatePhotoTextModels;
				},
				[] as EditorInteractiveInteractionTemplatePhotoModel[],
			);
	}

	protected get interactionTemplatePhotoTextModelClasses(): (templatePosition: EditorInteractiveInteractionTemplatePhotoModel['templatePosition'] | EditorInteractiveInteractionTemplateTextModel['templatePosition']) => Record<string, boolean> {
		return (templatePosition) => ({
			'editor-interactive-view-add-photo': templatePosition.type === 'photo',
			'editor-interactive-view-add-text': templatePosition.type === 'text',
		});
	}

	protected get interactionTemplatePhotoTextModelStyles(): (objectVars: PageObjectVars) => Partial<CSSStyleDeclaration> & Record<string, string> {
		return (objectVars) => {
			const styles: Partial<CSSStyleDeclaration> & Record<string, string> = {};

			if (this.$el) {
				let addImageTextFontSize: number;
				let addImageTextIconSize: number;
				let addImageTextRowGap: number;

				if (!this.isMobile) {
					addImageTextFontSize = parseInt(
						getComputedStyle(this.$el).getPropertyValue('--font-size-l'),
						10,
					);
					addImageTextIconSize = parseInt(
						getComputedStyle(this.$el).getPropertyValue('--font-size-x3l'),
						10,
					);
					addImageTextRowGap = 16;
				} else {
					addImageTextFontSize = parseInt(
						getComputedStyle(this.$el).getPropertyValue('--font-size-s'),
						10,
					);
					addImageTextIconSize = parseInt(
						getComputedStyle(this.$el).getPropertyValue('--font-size-x2l'),
						10,
					);
					addImageTextRowGap = 8;
				}

				let height = Math.round(
					Math.min(
						objectVars.height * this.scaling,
						objectVars.canvas.height,
					),
				);
				let width = Math.round(
					Math.min(
						objectVars.width * this.scaling,
						objectVars.canvas.width,
					),
				);
				let marginLeft = Math.round(objectVars.placement.x * this.scaling);
				let marginTop = Math.round(objectVars.placement.y * this.scaling);
				const addImageFontSizeScaleFactor = (addImageTextFontSize + addImageTextIconSize + addImageTextRowGap) / addImageTextFontSize;
				const addImageIconSizeScaleFactor = (addImageTextFontSize + addImageTextIconSize + addImageTextRowGap) / addImageTextIconSize;
				const addImageRowGapScaleFactor = (addImageTextFontSize + addImageTextIconSize + addImageTextRowGap) / addImageTextRowGap;

				if (height < (addImageTextFontSize + addImageTextIconSize + addImageTextRowGap)) {
					addImageTextFontSize = Math.round(height / addImageFontSizeScaleFactor);
					addImageTextIconSize = Math.round(height / addImageIconSizeScaleFactor);
					addImageTextRowGap = Math.round(height / addImageRowGapScaleFactor);
				}

				if (addImageTextFontSize < 10) {
					addImageTextFontSize = 10;
					addImageTextIconSize = 20;
					addImageTextRowGap = 0;
				}

				if (marginLeft < 0) {
					width -= Math.abs(marginLeft);
					marginLeft = 0;
				}
				if (marginTop < 0) {
					height -= Math.abs(marginTop);
					marginTop = 0;
				}

				styles['--add-image-text-font-size'] = `${addImageTextFontSize}px`;
				styles['--add-image-text-icon-size'] = `${addImageTextIconSize}px`;
				styles['--add-image-text-row-gap'] = `${addImageTextRowGap}px`;
				styles.height = `${height}px`;
				styles.marginLeft = `${marginLeft}px`;
				styles.marginTop = `${marginTop}px`;
				styles.transform = `rotate(${objectVars.rotate}deg)`;
				styles.width = `${width}px`;
			}

			return styles;
		};
	}

	protected get interactionTextObjectModelStyles(): (objectModel: EditorInteractivePageObjectModel) => Partial<CSSStyleDeclaration> {
		return (objectModel: EditorInteractivePageObjectModel): Partial<CSSStyleDeclaration> => {
			const styles: Partial<CSSStyleDeclaration> = {};

			if (objectModel.align === 'Center') {
				styles.textAlign = 'center';
			} else if (objectModel.align === 'Right') {
				styles.textAlign = 'right';
			} else if (objectModel.align === 'Left') {
				styles.textAlign = 'left';
			}

			if (objectModel.pointsize) {
				const fontSizeScaled = ((objectModel.pointsize * 31496) / 15120) * this.scaling;
				styles.fontSize = `${fontSizeScaled}px`;
				styles.lineHeight = `${fontSizeScaled * 1.25}px`;
			}

			styles.fontFamily = objectModel.fontface || '';
			styles.fontStyle = objectModel.fontitalic ? 'italic' : '';
			styles.fontWeight = objectModel.fontbold ? 'bold' : '';
			styles.textDecoration = objectModel.fontunderline ? 'underline' : '';

			return styles;
		};
	}

	protected get interactionTextObjectModelText(): (objectModel: EditorInteractivePageObjectModel) => string {
		return (objectModel: EditorInteractivePageObjectModel): string => {
			if (
				objectModel?.text_formatted
				&& objectModel.text
			) {
				let newFormattedText = objectModel.text;
				const formattedNewLines = Array.from(objectModel.text_formatted.matchAll(/\n/g));

				// eslint-disable-next-line no-restricted-syntax
				for (const formattedNewLine of formattedNewLines) {
					if (formattedNewLine.index) {
						const firstPartOfNewText = newFormattedText.substring(
							0,
							formattedNewLine.index,
						);
						const secondPartOfNewText = newFormattedText.substring(formattedNewLine.index);
						const secondPartPlusOneCharacterOfNewText = newFormattedText.substring(formattedNewLine.index + 1);
						const firstCharacterOfNewText = newFormattedText.substring(
							formattedNewLine.index,
							formattedNewLine.index + 1,
						);
						const secondCharacterOfNewText = newFormattedText.substring(
							formattedNewLine.index + 1,
							formattedNewLine.index + 2,
						);
						const secondCharacterOfOldTextFormatted = objectModel.text_formatted.substring(
							formattedNewLine.index + 1,
							formattedNewLine.index + 2,
						);

						if (firstCharacterOfNewText !== '\n') {
							const isCurrentTextCharacterSpace = /^\s$/.test(firstCharacterOfNewText);
							let lastPartOfText = '';

							if (isCurrentTextCharacterSpace) {
								lastPartOfText = `${secondPartPlusOneCharacterOfNewText}`;
							} else {
								lastPartOfText = secondPartOfNewText;
							}

							newFormattedText = `${firstPartOfNewText}\r${lastPartOfText}`;
						} else if (
							secondCharacterOfNewText !== ' '
							&& secondCharacterOfOldTextFormatted === ' '
						) {
							newFormattedText = `${firstPartOfNewText}\r ${secondPartPlusOneCharacterOfNewText}`;
						} else {
							newFormattedText = `${firstPartOfNewText}\r${secondPartPlusOneCharacterOfNewText}`;
						}
					}
				}

				const newFormattedTextLines = newFormattedText.split('\r');

				if (newFormattedTextLines.length < 1) {
					return '';
				}

				let htmlContent = '';

				// eslint-disable-next-line no-restricted-syntax
				for (const computedTextObjectTextLine of newFormattedTextLines) {
					const spanElement = document.createElement('span');

					if (objectModel.pointsize) {
						const fontSizeScaled = ((objectModel.pointsize * 31496) / 15120) * this.scaling;
						spanElement.style.height = `${fontSizeScaled * 1.25}px`;
					}

					if (objectModel.align === 'Left') {
						spanElement.style.justifyContent = 'safe flex-start';
					} else if (objectModel.align === 'Center') {
						spanElement.style.justifyContent = 'safe center';
					} else if (objectModel.align === 'Right') {
						spanElement.style.justifyContent = 'safe flex-end';
					}

					spanElement.innerHTML = computedTextObjectTextLine;
					htmlContent += spanElement.outerHTML;
					spanElement.remove();
				}

				return htmlContent;
			}

			return '';
		};
	}

	protected get interactionTextObjectModels(): EditorInteractiveInteractionPageObjectModel[] {
		return this.internalPageObjects
			.filter((pageObject) => pageObject.type === 'text')
			.sort((a, b) => a.z_axis - b.z_axis)
			.map((pageObject) => ({
				objectVars: PageObject.calculatePosition(
					{
						x_axis: pageObject.x_axis,
						y_axis: pageObject.y_axis,
						width: pageObject.width,
						height: pageObject.height,
						borderwidth: pageObject.borderwidth,
						bordercolor: pageObject.bordercolor,
						cropx: pageObject.cropx,
						cropy: pageObject.cropy,
						cropwidth: pageObject.cropwidth,
						cropheight: pageObject.cropheight,
						angle: pageObject.rotate,
						flop: Boolean(pageObject.flop),
						flip: Boolean(pageObject.flip),
						type: pageObject.type,
						maxwidth: pageObject.maxwidth,
						photoid: pageObject.photoid || undefined,
					},
					this.scaling,
					null,
					this.bleedMargin,
				),
				object: pageObject,
			}));
	}

	protected get isObjectDeletable(): (objectModel: EditorInteractivePageObjectModel) => boolean {
		return (objectModel: EditorInteractivePageObjectModel): boolean => (
			objectModel.type === 'text'
			&& (
				!!objectModel._selected
				|| (
					!!objectModel._selectedForEdition
					&& !this.multipleTextSupport
				)
			)
		);
	}

	protected get isObjectEditable(): (objectModel: EditorInteractivePageObjectModel) => boolean {
		return (objectModel: EditorInteractivePageObjectModel): boolean => (
			!!objectModel._selectedForEdition
			&& objectModel.type === 'text'
			&& !this.multipleTextSupport
		);
	}

	private get isObjectHovered(): (objectModel: EditorInteractivePageObjectModel) => boolean {
		return (objectModel: EditorInteractivePageObjectModel): boolean => !!objectModel._hovered;
	}

	protected get isObjectRotable(): (objectModel: EditorInteractivePageObjectModel) => boolean {
		return (objectModel: EditorInteractivePageObjectModel): boolean => !!objectModel._selectedForEdition;
	}

	private get isObjectSelected(): (objectModel: EditorInteractivePageObjectModel) => boolean {
		return (objectModel: EditorInteractivePageObjectModel): boolean => !!objectModel._selected;
	}

	private get isObjectSelectedForEdition(): (objectModel: EditorInteractivePageObjectModel) => boolean {
		return (objectModel: EditorInteractivePageObjectModel): boolean => !!objectModel._selectedForEdition;
	}

	protected get isObjectTextObject(): (objectModel: EditorInteractivePageObjectModel) => boolean {
		return (objectModel: EditorInteractivePageObjectModel): boolean => objectModel.type === 'text';
	}

	protected get isObjectTransformable(): (objectModel: EditorInteractivePageObjectModel) => boolean {
		return (objectModel: EditorInteractivePageObjectModel): boolean => (
			objectModel.type === 'text'
			|| this.isPhotoObjectTransformable
		);
	}

	private get selectedObjectForEdition(): EditorInteractivePageObjectModel | undefined {
		return this.internalPageObjects.find((pageObject) => !!pageObject._selectedForEdition);
	}

	private get selectedObjectForEditionElement(): HTMLElement | undefined {
		if (!this.$el) {
			return undefined;
		}

		if (this.selectedObjectForEdition) {
			return (
				this.$el.querySelector(`.editor-interactive-view-edit-object[data-object-id="${this.selectedObjectForEdition.id}"]`) as HTMLElement
				|| undefined
			);
		}

		return undefined;
	}

	private get selectedPageObjectElement(): HTMLElement | undefined {
		if (!this.$el) {
			return undefined;
		}

		let selectedPageObject: EditorInteractivePageObjectModel | undefined;

		if (
			this.selectedPhotoObjectModel
			&& !this.selectedTextObjectModel
		) {
			selectedPageObject = this.selectedPhotoObjectModel;
		} else if (this.selectedTextObjectModel) {
			selectedPageObject = this.selectedTextObjectModel;
		}

		if (selectedPageObject) {
			return (
				this.$el.querySelector(`.editor-interactive-view-edit-object[data-object-id="${selectedPageObject.id}"]`) as HTMLElement
				|| undefined
			);
		}

		return undefined;
	}

	private get selectedPhotoObjectModel(): EditorInteractivePageObjectModel | undefined {
		return this.internalPageObjects.find((pageObject) => (
			pageObject.type === 'photo'
			&& !!pageObject._selected
		));
	}

	private get selectedTextObjectModel(): EditorInteractivePageObjectModel | undefined {
		return this.internalPageObjects.find((pageObject) => (
			pageObject.type === 'text'
			&& !!pageObject._selected
		));
	}

	protected get shouldShowObjectResizeHandle(): (
		objectModel: EditorInteractivePageObjectModel,
		side: EditorInteractivePageObjectResizeSide,
	) => boolean {
		return (
			objectModel: EditorInteractivePageObjectModel,
			side: EditorInteractivePageObjectResizeSide,
		): boolean => {
			if (side === 'top-left') {
				return (
					this.isObjectSelectedForEdition(objectModel)
					&& !this.isObjectEditable(objectModel)
				);
			}
			if (side === 'top-right') {
				return (
					(
						this.isObjectSelectedForEdition(objectModel)
						|| (
							objectModel.type === 'text'
							&& this.isObjectSelected(objectModel)
						)
					)
					&& !this.isObjectDeletable(objectModel)
				);
			}

			return this.isObjectSelectedForEdition(objectModel);
		};
	}

	@Ref('editorTextOptionsView')
	private editorTextOptionsViewComponent!: EditorTextOptionsView;

	private editorTextOptionsTooltips!: Record<EditorInteractivePageObjectModel['id'], ServiceOpenReturn<TooltipComponent<typeof EditorTextOptionsView>>>;

	private internalIsEyeDropperShown = false;

	private internalPageObjects: EditorInteractivePageObjectModels = [];

	protected isMobile = mobileTools.isMobile;

	private isMobileUnwatch?: () => void;

	private isPinch?: boolean;

	private objectContentMouseDown?: boolean;

	private objectEditorContentRotateCenterX!: number;

	private objectEditorContentRotateCenterY!: number;

	private objectEditorContentX!: number;

	private objectEditorContentY!: number;

	private objectResizeMouseDown?: boolean;

	private objectResizeWidthMouseDown?: boolean;

	private objectResizeSide?: EditorInteractivePageObjectResizeSide;

	private objectRotateMouseDown?: boolean;

	protected renderEditObjectList = true;

	private windowClickHandlers!: Record<EditorInteractivePageObjectModel['id'], (event: MouseEvent) => void>;

	protected beforeDestroy(): void {
		const windowClickHandlers = Object.entries(this.windowClickHandlers);

		// eslint-disable-next-line no-restricted-syntax
		for (const [objectId, windowClickHandler] of windowClickHandlers) {
			window.removeEventListener(
				'click',
				windowClickHandler,
			);
			delete this.windowClickHandlers[objectId];
		}

		this.destroyEditorTextOptionsTooltips();
		this.isMobileUnwatch?.();
		window.removeEventListener(
			'mousemove',
			this.onWindowMouseMove,
		);
		window.removeEventListener(
			'touchmove',
			this.onWindowMouseMove,
		);
		window.removeEventListener(
			'mouseup',
			this.onWindowMouseUp,
		);
		window.removeEventListener(
			'touchend',
			this.onWindowMouseUp,
		);
	}

	protected created(): void {
		this.editorTextOptionsTooltips = {};
		this.isMobileUnwatch = mobileTools.watch(() => {
			this.isMobile = mobileTools.isMobile;
		});
		this.windowClickHandlers = {};
	}

	protected mounted(): void {
		this.$forceCompute('selectedObjectForEditionElement');
		this.$forceCompute('selectedPageObjectElement');
	}

	@Watch(
		'isEyeDropperShown',
		{
			immediate: true,
		},
	)
	protected onIsEyeDropperShownChange(): void {
		this.internalIsEyeDropperShown = this.isEyeDropperShown || false;
	}

	@Watch(
		'pageObjects',
		{
			deep: true,
			immediate: true,
		},
	)
	protected onPageObjectsChange(): void {
		let objectAdded = false;
		const newObjects = this.pageObjects;
		const oldObjects = this.internalPageObjects;
		const objectsToRemove: EditorInteractivePageObjectModel[] = [];

		// eslint-disable-next-line no-restricted-syntax
		for (const newObject of newObjects) {
			const oldObjectFound = oldObjects.find((oldObject) => oldObject.id === newObject.id);

			if (!oldObjectFound) {
				objectAdded = true;
				oldObjects.push({
					...newObject,
				});
			} else {
				const objectDifferences = objectUtils.getObjectDifferences(
					newObject,
					oldObjectFound,
				);

				if (objectDifferences.length) {
					// eslint-disable-next-line no-restricted-syntax
					for (const objectDifference of objectDifferences) {
						(oldObjectFound as any)[objectDifference] = newObject[objectDifference];
					}
				}
			}
		}

		// eslint-disable-next-line no-restricted-syntax
		for (const oldObject of oldObjects) {
			const newObjectFound = newObjects.find((newObject) => newObject.id === oldObject.id);

			if (!newObjectFound) {
				objectsToRemove.push(oldObject);
			}
		}

		// eslint-disable-next-line no-restricted-syntax
		for (const objectToRemove of objectsToRemove) {
			oldObjects.splice(
				oldObjects.indexOf(objectToRemove),
				1,
			);
		}

		if (objectAdded) {
			this.forceReRenderEditObjectList();
		}
	}

	@Watch('selectedObjectForEdition')
	protected onSelectedObjectForEditionChange(
		newSelectedObjectForEdition?: EditorInteractivePageObjectModel,
		oldSelectedObjectForEdition?: EditorInteractivePageObjectModel,
	): void {
		requestAnimationFrame(() => {
			if (
				!this.multipleTextSupport
				&& newSelectedObjectForEdition?.type === 'text'
				&& this.selectedObjectForEditionElement
			) {
				this.openEditorTextOptionsTooltip(
					this.selectedObjectForEditionElement,
					newSelectedObjectForEdition,
				);
			} else if (
				!this.multipleTextSupport
				&& !this.selectedObjectForEditionElement
			) {
				this.$nextTick(() => this.destroyEditorTextOptionsTooltips(newSelectedObjectForEdition?.id));
			}

			if (oldSelectedObjectForEdition) {
				this.removeWindowClickHandler(oldSelectedObjectForEdition);
				this.checkTextObjectChanges(oldSelectedObjectForEdition);
			} else {
				requestAnimationFrame(() => {
					if (oldSelectedObjectForEdition) {
						this.removeWindowClickHandler(oldSelectedObjectForEdition);
					}
					if (newSelectedObjectForEdition) {
						this.addWindowClickHandler(newSelectedObjectForEdition);
					}
				});
			}
		});
	}

	@Watch('selectedTextObjectModel')
	protected onSelectedTextObjectModelChange(
		newSelectedTextObjectModel?: EditorInteractivePageObjectModel,
		oldSelectedTextObjectModel?: EditorInteractivePageObjectModel,
	): void {
		if (oldSelectedTextObjectModel?.id !== newSelectedTextObjectModel?.id) {
			this.$nextTick(() => {
				this.destroyEditorTextOptionsTooltips(newSelectedTextObjectModel?.id);

				if (
					this.selectedPageObjectElement
					&& newSelectedTextObjectModel
				) {
					if (this.selectedTextObjectModel) {
						const selectedTextObjectTextContentElement = this.selectedPageObjectElement.querySelector<HTMLSpanElement>('.editor-interactive-view-edit-object-text-content');

						if (selectedTextObjectTextContentElement) {
							selectedTextObjectTextContentElement.focus();
							selectedTextObjectTextContentElement.innerHTML = this.interactionTextObjectModelText(this.selectedTextObjectModel);
							domUtils.setCaretPosition(
								selectedTextObjectTextContentElement,
								selectedTextObjectTextContentElement.textContent?.length || 0,
							);
						} else {
							this.onSelectedTextObjectModelChange(
								newSelectedTextObjectModel,
								oldSelectedTextObjectModel,
							);
							return;
						}

						this.openEditorTextOptionsTooltip(
							this.selectedPageObjectElement,
							newSelectedTextObjectModel,
						);
					}

					if (!mobileTools.isMobile) {
						requestAnimationFrame(() => this.addWindowClickHandler(newSelectedTextObjectModel));
					}
				}
				if (oldSelectedTextObjectModel) {
					this.removeWindowClickHandler(oldSelectedTextObjectModel);
					this.checkTextObjectChanges(oldSelectedTextObjectModel);
				}
			});
		}
	}

	private addWindowClickHandler(objectModel: EditorInteractivePageObjectModel): void {
		if (!this.windowClickHandlers[objectModel.id]) {
			const handler = (event: MouseEvent) => this.onWindowClick(
				objectModel.id,
				event,
			);
			window.addEventListener(
				'click',
				handler,
			);
			this.windowClickHandlers[objectModel.id] = handler;
		}
	}

	private checkTextObjectChanges(objectModel: EditorInteractivePageObjectModel): void {
		if (objectModel.type === 'text') {
			if (!objectModel.text) {
				this.$emit(
					'delete-object',
					objectModel,
				);
			} else {
				// TODO: add logic to check if text object has pending changes
				this.$emit('push-changes');
			}
		}
	}

	private destroyEditorTextOptionsTooltips(objectIdToOmit?: EditorInteractivePageObjectModel['id']): void {
		if (this.editorTextOptionsTooltips) {
			const editorTextOptionsTooltips = Object.entries(this.editorTextOptionsTooltips);

			// eslint-disable-next-line no-restricted-syntax
			for (const [objectId, tooltip] of editorTextOptionsTooltips) {
				if (objectId !== objectIdToOmit) {
					tooltip.destroy();
					delete this.editorTextOptionsTooltips[objectId];
				}
			}
		}
	}

	private async forceReRenderEditObjectList(onlyCheckForTextElement?: boolean): Promise<void> {
		if (!onlyCheckForTextElement) {
			this.renderEditObjectList = false;
			await this.$nextTick();
			this.renderEditObjectList = true;
			await this.$nextTick();
		}

		if (this.selectedTextObjectModel) {
			this.destroyEditorTextOptionsTooltips(this.selectedTextObjectModel.id);
			const selectedPageObjectElement = this.$el.querySelector<HTMLElement>(`.editor-interactive-view-edit-object[data-object-id="${this.selectedTextObjectModel.id}"]`);
			const selectedTextObjectTextContentElement = selectedPageObjectElement?.querySelector<HTMLSpanElement>('.editor-interactive-view-edit-object-text-content');

			if (
				!selectedPageObjectElement
				|| !selectedTextObjectTextContentElement
			) {
				requestAnimationFrame(() => {
					this.forceReRenderEditObjectList(true);
				});
				return;
			}

			if (selectedTextObjectTextContentElement) {
				selectedTextObjectTextContentElement.focus();
				selectedTextObjectTextContentElement.innerHTML = this.interactionTextObjectModelText(this.selectedTextObjectModel);
				domUtils.setCaretPosition(
					selectedTextObjectTextContentElement,
					selectedTextObjectTextContentElement.textContent?.length || 0,
				);
			}

			this.openEditorTextOptionsTooltip(
				selectedPageObjectElement,
				this.selectedTextObjectModel,
			);

			if (!mobileTools.isMobile) {
				const { selectedTextObjectModel } = this;
				this.removeWindowClickHandler(selectedTextObjectModel);
				requestAnimationFrame(() => this.addWindowClickHandler(selectedTextObjectModel));
			}
		}
	}

	protected onAddPhotoTextClick(templatePosition: EditorInteractiveInteractionTemplatePhotoModel['templatePosition'] | EditorInteractiveInteractionTemplateTextModel['templatePosition']): void {
		if (templatePosition.type === 'photo') {
			this.$emit(
				'add-photo',
				templatePosition,
			);
		} else if (templatePosition.type === 'text') {
			this.$emit(
				'add-text',
				templatePosition,
			);
		}
	}

	protected onEditClick(pageObject: EditorInteractivePageObjectModel): void {
		if (
			this.selectedObjectForEdition?.id !== pageObject.id
			&& !pageObject._selected
		) {
			const objectsDifferences: Record<EditorInteractivePageObjectModel['id'], Array<keyof EditorInteractivePageObjectModel>> = {
				[pageObject.id]: ['_selectedForEdition'],
			};
			pageObject._selectedForEdition = true;
			this.$emit(
				'change',
				this.internalPageObjects,
				objectsDifferences,
			);
			requestAnimationFrame(() => this.addWindowClickHandler(pageObject));
		}
	}

	protected onEditDoubleClick(pageObject: EditorInteractivePageObjectModel): void {
		if (!pageObject._selected) {
			// TODO: check if this is actually necessary, basically if the click event happens and this is already removed
			this.removeWindowClickHandler(pageObject);
			const objectsDifferences: Record<EditorInteractivePageObjectModel['id'], Array<keyof EditorInteractivePageObjectModel>> = {
				[pageObject.id]: [
					'_selected',
					'_selectedForEdition',
				],
			};
			pageObject._selectedForEdition = false;
			pageObject._selected = true;

			if (pageObject.type === 'text') {
				pageObject._previousState = {
					...pageObject,
				};
				objectsDifferences[pageObject.id].push('_previousState');
			}

			this.$emit(
				'change',
				this.internalPageObjects,
				objectsDifferences,
			);
		}
	}

	protected onObjectDeleteClick(pageObject: EditorInteractivePageObjectModel): void {
		this.$emit(
			'delete-object',
			pageObject,
		);
	}

	protected onObjectEditClick(pageObject: EditorInteractivePageObjectModel): void {
		if (!pageObject._selected) {
			const objectsDifferences: Record<EditorInteractivePageObjectModel['id'], Array<keyof EditorInteractivePageObjectModel>> = {
				[pageObject.id]: [
					'_selected',
					'_selectedForEdition',
				],
			};
			pageObject._selectedForEdition = false;
			pageObject._selected = true;

			if (pageObject.type === 'text') {
				pageObject._previousState = {
					...pageObject,
				};
				objectsDifferences[pageObject.id].push('_previousState');
			}

			this.$emit(
				'change',
				this.internalPageObjects,
				objectsDifferences,
			);
		}
	}

	protected onObjectMouseDown(
		objectModel: EditorInteractivePageObjectModel,
		event: MouseEvent | TouchEvent,
	): void {
		if (
			!event.defaultPrevented
			&& (
				objectModel.type !== 'text'
				|| !objectModel._selected
			)
		) {
			let finalEvent: MouseEvent | Touch | Touch[];

			if ('touches' in event) {
				if (event.touches.length == 2) {
					finalEvent = Array.from(event.touches);
				} else {
					// eslint-disable-next-line prefer-destructuring
					finalEvent = event.touches[0];
				}
			} else {
				finalEvent = event;
			}

			if (!Array.isArray(finalEvent)) {
				this.objectEditorContentX = finalEvent.clientX;
				this.objectEditorContentY = finalEvent.clientY;
			} else {
				this.objectEditorContentX = finalEvent[1].clientX - finalEvent[0].clientX;
				this.objectEditorContentY = finalEvent[1].clientY - finalEvent[0].clientY;
			}

			this.objectContentMouseDown = true;
			const wasPhotoSelected = !!this.selectedPhotoObjectModel;

			const logic = () => {
				if (
					(
						(
							!wasPhotoSelected
							&& !this.selectedPhotoObjectModel
						)
						|| (
							wasPhotoSelected
							&& this.selectedObjectForEdition?.type === 'text'
						)
					)
					&& !this.selectedTextObjectModel
				) {
					this.isPinch = Array.isArray(finalEvent);
					window.addEventListener(
						'mousemove',
						this.onWindowMouseMove,
					);
					window.addEventListener(
						'touchmove',
						this.onWindowMouseMove,
						{
							passive: false,
						},
					);
					window.addEventListener(
						'mouseup',
						this.onWindowMouseUp,
					);
					window.addEventListener(
						'touchend',
						this.onWindowMouseUp,
					);
				} else {
					this.objectContentMouseDown = false;
				}
			};

			if (this.isMobile) {
				setTimeout(
					logic,
					doubleTapInterval,
				);
			} else {
				logic();
			}
		}
	}

	protected onObjectResizeMouseDown(
		event: MouseEvent | TouchEvent,
		side: EditorInteractivePageObjectResizeSide,
	): void {
		event.preventDefault();
		let finalEvent: MouseEvent | Touch;

		if ('touches' in event) {
			// eslint-disable-next-line prefer-destructuring
			finalEvent = event.touches[0];
		} else {
			finalEvent = event;
		}

		this.objectEditorContentX = finalEvent.clientX;
		this.objectEditorContentY = finalEvent.clientY;
		this.objectResizeMouseDown = true;
		this.objectResizeSide = side;
		window.addEventListener(
			'mousemove',
			this.onWindowMouseMove,
		);
		window.addEventListener(
			'touchmove',
			this.onWindowMouseMove,
		);
		window.addEventListener(
			'mouseup',
			this.onWindowMouseUp,
		);
		window.addEventListener(
			'touchend',
			this.onWindowMouseUp,
		);
	}

	protected onObjectResizeWidthMouseDown(
		event: MouseEvent | TouchEvent,
		side: EditorInteractivePageObjectResizeSide,
	): void {
		event.preventDefault();
		let finalEvent: MouseEvent | Touch;

		if ('touches' in event) {
			// eslint-disable-next-line prefer-destructuring
			finalEvent = event.touches[0];
		} else {
			finalEvent = event;
		}

		this.objectEditorContentX = finalEvent.clientX;
		this.objectResizeWidthMouseDown = true;
		this.objectResizeSide = side;
		window.addEventListener(
			'mousemove',
			this.onWindowMouseMove,
		);
		window.addEventListener(
			'touchmove',
			this.onWindowMouseMove,
		);
		window.addEventListener(
			'mouseup',
			this.onWindowMouseUp,
		);
		window.addEventListener(
			'touchend',
			this.onWindowMouseUp,
		);
	}

	protected onObjectRotateMouseDown(event: MouseEvent | TouchEvent): void {
		const eventTarget = event.target as HTMLDivElement;
		event.preventDefault();
		let finalEvent: MouseEvent | Touch;

		if ('touches' in event) {
			// eslint-disable-next-line prefer-destructuring
			finalEvent = event.touches[0];
		} else {
			finalEvent = event;
		}

		const editorInteractiveViewEditObjectElement = eventTarget.closest('.editor-interactive-view-edit-object') as HTMLDivElement;
		const editorInteractiveViewEditObjectElementRect = editorInteractiveViewEditObjectElement.getBoundingClientRect();
		this.objectEditorContentRotateCenterX = editorInteractiveViewEditObjectElementRect.left + (editorInteractiveViewEditObjectElementRect.width / 2);
		this.objectEditorContentRotateCenterY = editorInteractiveViewEditObjectElementRect.top + (editorInteractiveViewEditObjectElementRect.height / 2);
		this.objectEditorContentX = finalEvent.clientX;
		this.objectEditorContentY = finalEvent.clientY;
		this.objectRotateMouseDown = true;
		window.addEventListener(
			'mousemove',
			this.onWindowMouseMove,
		);
		window.addEventListener(
			'touchmove',
			this.onWindowMouseMove,
		);
		window.addEventListener(
			'mouseup',
			this.onWindowMouseUp,
		);
		window.addEventListener(
			'touchend',
			this.onWindowMouseUp,
		);
	}

	protected async onTextObjectContentInput(
		textObject: EditorInteractivePageObjectModel,
		event: Event,
	): Promise<void> {
		if (
			textObject.fontface
			&& event.target
		) {
			const fontModel = FontModule.getById(textObject.fontface);

			if (!fontModel) {
				throw new Error('Could not find required font model');
			}

			event.preventDefault();
			const targetElement = event.target as HTMLElement;
			const subset = fontModel.subset.split(',');
			const oldText = textObject.text || '';
			const editorModuleTextEditorContentElement = (
				targetElement.closest('.editor-interactive-view-edit-object-text-content')
				|| targetElement.querySelector('.editor-interactive-view-edit-object-text-content')
			) as HTMLSpanElement;
			let cursorPosition = domUtils.getCaretPosition(editorModuleTextEditorContentElement);

			if (typeof editorModuleTextEditorContentElement.textContent?.length !== 'undefined') {
				cursorPosition -= editorModuleTextEditorContentElement.textContent.length + 1;
			}

			let newText = editorModuleTextEditorContentElement.innerText;
			const newTextLines = Array.from(newText.matchAll(/\n/g));

			if (
				editorModuleTextEditorContentElement.firstChild
				&& !(editorModuleTextEditorContentElement.firstChild instanceof HTMLSpanElement)
			) {
				editorModuleTextEditorContentElement.firstChild.remove();
			}

			let lengthEdited = 0;

			// eslint-disable-next-line no-restricted-syntax
			for (const newTextLine of newTextLines) {
				if (newTextLine.index) {
					const firstPartOfNewText = newText.substring(
						0,
						newTextLine.index - lengthEdited,
					);
					const secondPartPlusOneCharacterOfNewText = newText.substring(newTextLine.index - lengthEdited + 1);
					const secondPartPlusTwoCharactersOfNewText = newText.substring(newTextLine.index - lengthEdited + 2);
					const secondCharacterOfNewText = newText.substring(
						newTextLine.index - lengthEdited + 1,
						newTextLine.index - lengthEdited + 2,
					);
					const firstCharacterOfOldText = oldText.substring(
						newTextLine.index,
						newTextLine.index + 1,
					);
					const secondCharacterOfOldText = oldText.substring(
						newTextLine.index + 1,
						newTextLine.index + 2,
					);

					if (
						firstCharacterOfOldText !== '\n'
						&& secondCharacterOfNewText !== '\n'
						&& secondCharacterOfNewText !== ''
						&& secondCharacterOfNewText !== ' '
						&& (
							newText.length <= (oldText.length + ((newTextLines.length) * 2))
							&& newText.length >= (oldText.length - 1)
						)
					) {
						if (firstCharacterOfOldText === ' ') {
							newText = `${firstPartOfNewText} ${secondPartPlusOneCharacterOfNewText}`;
						} else {
							lengthEdited += 1;
							newText = `${firstPartOfNewText}${secondPartPlusOneCharacterOfNewText}`;
						}
					} else if (
						firstCharacterOfOldText !== '\n'
						&& secondCharacterOfNewText === '\n'
						&& (newText.length - 1) === oldText.length
					) {
						newText = `${firstPartOfNewText}${secondPartPlusTwoCharactersOfNewText}`;
					} else if (
						firstCharacterOfOldText !== '\n'
						&& secondCharacterOfNewText === '\n'
					) {
						newText = `${firstPartOfNewText}\n${secondPartPlusTwoCharactersOfNewText}`;
					} else if (
						firstCharacterOfOldText === '\n'
						&& secondCharacterOfOldText !== '\n'
						&& secondCharacterOfNewText === '\n'
					) {
						newText = `${firstPartOfNewText}\n${secondPartPlusTwoCharactersOfNewText}`;
					}
				}
			}

			if (/^\n$/.test(newText)) {
				newText = '';
			}

			const text = filterText(
				newText,
				subset,
			);
			const updatedTextObjectResult = await ProductStateModule.changePageObject({
				id: textObject.id,
				text,
				noStore: true,
				originalPageObject: textObject,
			});

			if (updatedTextObjectResult) {
				this.internalPageObjects.splice(
					this.internalPageObjects.indexOf(textObject),
					1,
					updatedTextObjectResult.updatedPageObject,
				);
				const { partialPageObject } = updatedTextObjectResult;
				const objectsDifferences: Record<EditorInteractivePageObjectModel['id'], Array<keyof EditorInteractivePageObjectModel>> = {
					[partialPageObject.id]: [
						...Object.keys(partialPageObject) as Array<keyof EditorInteractivePageObjectModel>,
					],
				};
				const objectDifferences = objectsDifferences[partialPageObject.id];
				objectDifferences.splice(
					objectDifferences.indexOf('id'),
					1,
				);
				this.$emit(
					'change',
					this.internalPageObjects,
					objectsDifferences,
				);
				this.updateTextObjectElementContent(cursorPosition);
			}
		}
	}

	private onWindowClick(
		objectId: EditorInteractivePageObjectModel['id'],
		event: MouseEvent | TouchEvent,
	): void {
		const objectModel = this.internalPageObjects.find((pageObject) => pageObject.id === objectId);

		if (objectModel) {
			const eventTarget = event.target as HTMLElement;
			const editorInteractiveViewEditObjectElement = (
				this.$el.querySelector(`.editor-interactive-view-edit-object[data-object-id="${objectModel.id}"]`) as HTMLElement
				|| undefined
			);

			if (this.isObjectSelectedForEdition(objectModel)) {
				if (
					(
						!editorInteractiveViewEditObjectElement
						|| (
							!editorInteractiveViewEditObjectElement.isSameNode(eventTarget)
							&& !editorInteractiveViewEditObjectElement.contains(eventTarget)
						)
					)
					&& !event.defaultPrevented
				) {
					objectModel._selectedForEdition = false;
					const objectsDifferences: Record<EditorInteractivePageObjectModel['id'], Array<keyof EditorInteractivePageObjectModel>> = {
						[objectModel.id]: ['_selectedForEdition'],
					};
					this.$emit(
						'change',
						this.internalPageObjects,
						objectsDifferences,
					);
					this.removeWindowClickHandler(objectModel);
				}
			} else if (
				(
					this.isObjectSelected(objectModel)
					&& objectModel.type === 'photo'
					&& !this.selectedTextObjectModel
				)
				|| this.selectedTextObjectModel
			) {
				if (
					(
						!editorInteractiveViewEditObjectElement
						|| (
							!editorInteractiveViewEditObjectElement.isSameNode(eventTarget)
							&& !editorInteractiveViewEditObjectElement.contains(eventTarget)
							&& !this.editorTextOptionsViewComponent?.$el?.isSameNode(eventTarget)
							&& !this.editorTextOptionsViewComponent?.$el?.contains(eventTarget)
						)
					)
					&& !event.defaultPrevented
				) {
					objectModel._selected = false;
					const objectsDifferences: Record<EditorInteractivePageObjectModel['id'], Array<keyof EditorInteractivePageObjectModel>> = {
						[objectModel.id]: ['_selected'],
					};
					this.$emit(
						'change',
						this.internalPageObjects,
						objectsDifferences,
					);
					this.removeWindowClickHandler(objectModel);
				}
			}
		}
	}

	private onWindowMouseMove(event: MouseEvent | TouchEvent): void {
		/**
		 * Minimum size limit for photo object to be (height and width)
		 */
		const minSizeLimit = 48 / this.scaling;
		let finalEvent: MouseEvent | Touch | Touch[];

		if ('touches' in event) {
			if (event.touches.length === 2) {
				finalEvent = Array.from(event.touches);

				if (!this.isPinch) {
					this.isPinch = true;
					this.objectEditorContentX = finalEvent[1].clientX - finalEvent[0].clientX;
					this.objectEditorContentY = finalEvent[1].clientY - finalEvent[0].clientY;
				}
			} else {
				// eslint-disable-next-line prefer-destructuring
				finalEvent = event.touches[0];

				if (this.isPinch) {
					this.isPinch = false;
					this.objectEditorContentX = finalEvent.clientX;
					this.objectEditorContentY = finalEvent.clientY;
				}
			}
		} else {
			finalEvent = event;
		}

		if (
			this.objectContentMouseDown
			&& this.selectedObjectForEdition
			&& this.pageModel
			&& !Array.isArray(finalEvent)
		) {
			event.preventDefault();
			const xDifference = finalEvent.clientX - this.objectEditorContentX;
			const yDifference = finalEvent.clientY - this.objectEditorContentY;

			let newXAxis = this.selectedObjectForEdition.x_axis + (xDifference / this.scaling);
			let newYAxis = this.selectedObjectForEdition.y_axis + (yDifference / this.scaling);

			if (newXAxis < (0 - this.bleedMargin)) {
				newXAxis = (0 - this.bleedMargin);
			} else if ((newXAxis + this.selectedObjectForEdition.width) > (this.pageModel.width + this.bleedMargin)) {
				newXAxis = (this.pageModel.width + this.bleedMargin) - this.selectedObjectForEdition.width;
			}

			if (newYAxis < (0 - this.bleedMargin)) {
				newYAxis = (0 - this.bleedMargin);
			} else if ((newYAxis + this.selectedObjectForEdition.height) > (this.pageModel.height + this.bleedMargin)) {
				newYAxis = (this.pageModel.height + this.bleedMargin) - this.selectedObjectForEdition.height;
			}

			const partialObject: OptionalExceptFor<EditorInteractivePageObjectModel, 'id'> = {
				id: this.selectedObjectForEdition.id,
			};
			let xAxisChanged = false;
			let yAxisChanged = false;

			if (this.selectedObjectForEdition.x_axis !== newXAxis) {
				xAxisChanged = true;
				partialObject.x_axis = newXAxis;
			}
			if (this.selectedObjectForEdition.y_axis !== newYAxis) {
				yAxisChanged = true;
				partialObject.y_axis = newYAxis;
			}

			if (
				xAxisChanged
				|| yAxisChanged
			) {
				const objectsDifferences: Record<EditorInteractivePageObjectModel['id'], Array<keyof EditorInteractivePageObjectModel>> = {
					[this.selectedObjectForEdition.id]: [],
				};
				const objectDifferences = Object.keys(partialObject) as (keyof EditorInteractivePageObjectModel)[];

				// eslint-disable-next-line no-restricted-syntax
				for (const objectDifference of objectDifferences) {
					if (objectDifference !== 'id') {
						objectsDifferences[this.selectedObjectForEdition.id].push(objectDifference);
						this.selectedObjectForEdition[objectDifference] = partialObject[objectDifference];
					}
				}

				this.$emit(
					'change',
					this.internalPageObjects,
					objectsDifferences,
				);

				if (!Array.isArray(finalEvent)) {
					if (xAxisChanged) {
						this.objectEditorContentX = finalEvent.clientX;
					}
					if (yAxisChanged) {
						this.objectEditorContentY = finalEvent.clientY;
					}
				} else {
					if (xAxisChanged) {
						this.objectEditorContentX = finalEvent[1].clientX - finalEvent[0].clientX;
					}
					if (yAxisChanged) {
						this.objectEditorContentY = finalEvent[1].clientY - finalEvent[0].clientY;
					}
				}
			}
		} else if (
			this.objectResizeMouseDown
			&& this.selectedObjectForEdition?.type === 'photo'
			&& this.pageModel
			&& !Array.isArray(finalEvent)
		) {
			event.preventDefault();
			let xDifference: number;
			let yDifference: number;

			if (
				this.objectResizeSide === 'bottom-left'
				|| this.objectResizeSide === 'bottom-right'
			) {
				yDifference = finalEvent.clientY - this.objectEditorContentY;
			} else {
				yDifference = this.objectEditorContentY - finalEvent.clientY;
			}

			if (
				this.objectResizeSide === 'bottom-left'
				|| this.objectResizeSide === 'top-left'
			) {
				xDifference = this.objectEditorContentX - finalEvent.clientX;
			} else {
				xDifference = finalEvent.clientX - this.objectEditorContentX;
			}

			let difference!: number;

			if (Math.abs(xDifference) > Math.abs(yDifference)) {
				difference = xDifference;
			} else {
				difference = yDifference;
			}

			const objectSizeRatio = this.selectedObjectForEdition.width / this.selectedObjectForEdition.height;
			let newWidth = this.selectedObjectForEdition.width + (difference / this.scaling);
			let newHeight = newWidth / objectSizeRatio;

			if (newWidth > this.pageModel.width) {
				newWidth = this.pageModel.width;
				newHeight = newWidth / objectSizeRatio;
			} else if (newHeight > this.pageModel.height) {
				newHeight = this.pageModel.height;
				newWidth = newHeight * objectSizeRatio;
			} else if (newWidth < 0) {
				newWidth = 0;
				newHeight = 0;
			} else if (newHeight < 0) {
				newHeight = 0;
				newWidth = 0;
			}

			const partialObject: OptionalExceptFor<EditorInteractivePageObjectModel, 'id'> = {
				id: this.selectedObjectForEdition.id,
			};
			let widthChanged = false;
			let heightChanged = false;

			if (this.selectedObjectForEdition.width !== newWidth) {
				widthChanged = true;
				partialObject.width = newWidth;
			}
			if (this.selectedObjectForEdition.height !== newHeight) {
				heightChanged = true;
				partialObject.height = newHeight;
			}

			if (typeof partialObject.width !== 'undefined') {
				if (
					partialObject.width < minSizeLimit
					&& this.selectedObjectForEdition.width > minSizeLimit
				) {
					partialObject.width = minSizeLimit;
					partialObject.height = minSizeLimit / objectSizeRatio;
				} else if (partialObject.width < minSizeLimit) {
					widthChanged = false;
					heightChanged = false;
					delete partialObject.width;
					delete partialObject.height;
					delete partialObject.x_axis;
					delete partialObject.y_axis;
				}
			}
			if (typeof partialObject.height !== 'undefined') {
				if (
					partialObject.height < minSizeLimit
					&& this.selectedObjectForEdition.height > minSizeLimit
				) {
					partialObject.height = minSizeLimit;
					partialObject.width = minSizeLimit * objectSizeRatio;
				} else if (partialObject.height < minSizeLimit) {
					heightChanged = false;
					widthChanged = false;
					delete partialObject.height;
					delete partialObject.width;
					delete partialObject.x_axis;
					delete partialObject.y_axis;
				}
			}

			if (
				widthChanged
				|| heightChanged
			) {
				if (this.objectResizeSide === 'top-left') {
					partialObject.x_axis = this.selectedObjectForEdition.x_axis + this.selectedObjectForEdition.width - (partialObject.width || 0);
					partialObject.y_axis = this.selectedObjectForEdition.y_axis + this.selectedObjectForEdition.height - (partialObject.height || 0);
				} else if (this.objectResizeSide === 'top-right') {
					partialObject.y_axis = this.selectedObjectForEdition.y_axis + this.selectedObjectForEdition.height - (partialObject.height || 0);
				} else if (this.objectResizeSide === 'bottom-left') {
					partialObject.x_axis = this.selectedObjectForEdition.x_axis + this.selectedObjectForEdition.width - (partialObject.width || 0);
				}

				const objectsDifferences: Record<EditorInteractivePageObjectModel['id'], Array<keyof EditorInteractivePageObjectModel>> = {
					[partialObject.id]: [],
				};
				const objectDifferences = Object.keys(partialObject) as (keyof EditorInteractivePageObjectModel)[];

				// eslint-disable-next-line no-restricted-syntax
				for (const objectDifference of objectDifferences) {
					if (objectDifference !== 'id') {
						objectsDifferences[partialObject.id].push(objectDifference);
						this.selectedObjectForEdition[objectDifference] = partialObject[objectDifference];
					}
				}

				this.$emit(
					'change',
					this.internalPageObjects,
					objectsDifferences,
				);

				if (widthChanged) {
					this.objectEditorContentX = finalEvent.clientX;
				}
				if (heightChanged) {
					this.objectEditorContentY = finalEvent.clientY;
				}
			}
		} else if (
			this.objectResizeWidthMouseDown
			&& this.selectedObjectForEdition?.type === 'photo'
			&& this.pageModel
			&& !Array.isArray(finalEvent)
		) {
			event.preventDefault();
			let xDifference: number;

			if (this.objectResizeSide === 'left') {
				xDifference = this.objectEditorContentX - finalEvent.clientX;
			} else {
				xDifference = finalEvent.clientX - this.objectEditorContentX;
			}

			const cropRatio = this.selectedObjectForEdition.cropwidth / this.selectedObjectForEdition.width;
			const unusedRightSpace = this.selectedObjectForEdition.maxwidth - this.selectedObjectForEdition.cropx - this.selectedObjectForEdition.cropwidth;
			let newCropwidth: number;
			let newCropx: number | undefined;

			if (this.objectResizeSide === 'right') {
				newCropwidth = this.selectedObjectForEdition.cropwidth + (xDifference / this.scaling);

				if (this.selectedObjectForEdition.cropx + newCropwidth > this.selectedObjectForEdition.maxwidth) {
					newCropwidth = this.selectedObjectForEdition.maxwidth - this.selectedObjectForEdition.cropx;
				} else if (newCropwidth < 0) {
					newCropwidth = 0;
				}
			} else {
				newCropx = this.selectedObjectForEdition.cropx - (xDifference / this.scaling);
				newCropwidth = this.selectedObjectForEdition.maxwidth - unusedRightSpace - newCropx;

				if (newCropx < 0) {
					newCropwidth -= Math.abs(newCropx);
					newCropx = 0;
				} else if ((newCropx + newCropwidth) > this.selectedObjectForEdition.maxwidth) {
					newCropx = this.selectedObjectForEdition.maxwidth;
					newCropwidth = 0;
				}
			}

			const partialObject: OptionalExceptFor<EditorInteractivePageObjectModel, 'id'> = {
				id: this.selectedObjectForEdition.id,
			};
			let cropwidthChanged = false;

			if (
				typeof newCropwidth !== 'undefined'
				&& this.selectedObjectForEdition.cropwidth !== newCropwidth
			) {
				cropwidthChanged = true;
				partialObject.cropwidth = newCropwidth;
			}
			if (
				typeof newCropx !== 'undefined'
				&& this.selectedObjectForEdition.cropx !== newCropx
			) {
				partialObject.cropx = newCropx;
			}

			if (typeof partialObject.cropwidth !== 'undefined') {
				if (
					partialObject.cropwidth < (minSizeLimit * cropRatio)
					&& this.selectedObjectForEdition.cropwidth > (minSizeLimit * cropRatio)
				) {
					partialObject.cropwidth = minSizeLimit * cropRatio;

					if (typeof partialObject.cropx !== 'undefined') {
						partialObject.cropx = this.selectedObjectForEdition.maxwidth - unusedRightSpace - partialObject.cropwidth;
					}
				} else if (partialObject.cropwidth < (minSizeLimit * cropRatio)) {
					cropwidthChanged = false;
					delete partialObject.cropwidth;
					delete partialObject.cropx;
					delete partialObject.width;
				}
			}

			if (
				cropwidthChanged
				&& partialObject.cropwidth
			) {
				partialObject.width = partialObject.cropwidth / cropRatio;

				if (this.objectResizeSide === 'left') {
					partialObject.x_axis = this.selectedObjectForEdition.x_axis + (this.selectedObjectForEdition.width - partialObject.width);
				}

				const objectsDifferences: Record<EditorInteractivePageObjectModel['id'], Array<keyof EditorInteractivePageObjectModel>> = {
					[partialObject.id]: [],
				};
				const objectDifferences = Object.keys(partialObject) as (keyof EditorInteractivePageObjectModel)[];

				// eslint-disable-next-line no-restricted-syntax
				for (const objectDifference of objectDifferences) {
					if (objectDifference !== 'id') {
						objectsDifferences[partialObject.id].push(objectDifference);
						this.selectedObjectForEdition[objectDifference] = partialObject[objectDifference];
					}
				}

				this.$emit(
					'change',
					this.internalPageObjects,
					objectsDifferences,
				);

				if (cropwidthChanged) {
					this.objectEditorContentX = finalEvent.clientX;
				}
			}
		} else if (
			(
				this.objectResizeMouseDown
				|| this.objectResizeWidthMouseDown
			)
			&& this.selectedObjectForEdition?.type === 'text'
			&& this.pageModel
			&& !Array.isArray(finalEvent)
		) {
			event.preventDefault();
			let xDifference: number;
			let yDifference: number | undefined;

			if (this.objectResizeMouseDown) {
				if (
					this.objectResizeSide === 'bottom-left'
					|| this.objectResizeSide === 'bottom-right'
				) {
					yDifference = finalEvent.clientY - this.objectEditorContentY;
				} else {
					yDifference = this.objectEditorContentY - finalEvent.clientY;
				}
			}

			if (
				this.objectResizeSide === 'bottom-left'
				|| this.objectResizeSide === 'left'
				|| this.objectResizeSide === 'top-left'
			) {
				xDifference = this.objectEditorContentX - finalEvent.clientX;
			} else {
				xDifference = finalEvent.clientX - this.objectEditorContentX;
			}

			let newWidth = this.selectedObjectForEdition.width + (xDifference / this.scaling);
			let newHeight: number | undefined;

			if (newWidth > this.pageModel.width) {
				newWidth = this.pageModel.width;
			} else if (newWidth < 0) {
				newWidth = 0;
			}

			if (yDifference) {
				newHeight = this.selectedObjectForEdition.height + (yDifference / this.scaling);

				if (newHeight > this.pageModel.height) {
					newHeight = this.pageModel.height;
				} else if (newHeight < 0) {
					newHeight = 0;
				}
			}

			const partialObject: OptionalExceptFor<EditorInteractivePageObjectModel, 'id'> = {
				id: this.selectedObjectForEdition.id,
			};
			let widthChanged = false;
			let heightChanged = false;

			if (this.selectedObjectForEdition.width !== newWidth) {
				widthChanged = true;
				partialObject.width = newWidth;
			}
			if (
				typeof newHeight !== 'undefined'
				&& this.selectedObjectForEdition.height !== newHeight
			) {
				heightChanged = true;
				partialObject.height = newHeight;
			}

			if (typeof partialObject.width !== 'undefined') {
				if (
					partialObject.width < minSizeLimit
					&& this.selectedObjectForEdition.width > minSizeLimit
				) {
					partialObject.width = minSizeLimit;
				} else if (partialObject.width < minSizeLimit) {
					widthChanged = false;
					delete partialObject.width;
				}
			}
			if (typeof partialObject.height !== 'undefined') {
				if (
					partialObject.height < minSizeLimit
					&& this.selectedObjectForEdition.height > minSizeLimit
				) {
					partialObject.height = minSizeLimit;
				} else if (partialObject.height < minSizeLimit) {
					heightChanged = false;
					delete partialObject.height;
				}
			}

			if (
				widthChanged
				|| heightChanged
			) {
				if (this.objectResizeSide === 'top-left') {
					if (partialObject.width) {
						partialObject.x_axis = this.selectedObjectForEdition.x_axis + this.selectedObjectForEdition.width - partialObject.width;
					} else if (partialObject.height) {
						partialObject.y_axis = this.selectedObjectForEdition.y_axis + this.selectedObjectForEdition.height - partialObject.height;
					}
				} else if (
					this.objectResizeSide === 'top-right'
					&& partialObject.height
				) {
					partialObject.y_axis = this.selectedObjectForEdition.y_axis + this.selectedObjectForEdition.height - partialObject.height;
				} else if (
					(
						this.objectResizeSide === 'bottom-left'
						|| this.objectResizeSide === 'left'
					)
					&& partialObject.width
				) {
					partialObject.x_axis = this.selectedObjectForEdition.x_axis + this.selectedObjectForEdition.width - partialObject.width;
				}

				if (this.selectedObjectForEdition.fontface) {
					const fontModel = FontModule.getById(this.selectedObjectForEdition.fontface);

					if (fontModel) {
						const fontSizeUp = resizeObjectText(
							{
								...this.selectedObjectForEdition,
								...partialObject,
							},
							fontModel,
							{
								resizeFont: {
									up: 300,
									down: 0,
								},
							},
						);
						const fontSizeDown = resizeObjectText(
							{
								...this.selectedObjectForEdition,
								...partialObject,
							},
							fontModel,
							{
								resizeFont: {
									up: 0,
									down: this.offeringModel?.minfontsize || 12,
								},
							},
						);

						if (
							fontSizeUp.pointsize
							&& this.selectedObjectForEdition.pointsize !== fontSizeUp.pointsize
						) {
							partialObject.pointsize = fontSizeUp.pointsize;
						} else if (
							fontSizeDown.pointsize
							&& this.selectedObjectForEdition.pointsize !== fontSizeDown.pointsize
						) {
							partialObject.pointsize = fontSizeDown.pointsize;
						}
					}
				}

				const objectsDifferences: Record<EditorInteractivePageObjectModel['id'], Array<keyof EditorInteractivePageObjectModel>> = {
					[partialObject.id]: [],
				};
				const objectDifferences = Object.keys(partialObject) as (keyof EditorInteractivePageObjectModel)[];

				// eslint-disable-next-line no-restricted-syntax
				for (const objectDifference of objectDifferences) {
					if (objectDifference !== 'id') {
						objectsDifferences[partialObject.id].push(objectDifference);
						this.selectedObjectForEdition[objectDifference] = partialObject[objectDifference];
					}
				}

				this.$emit(
					'change',
					this.internalPageObjects,
					objectsDifferences,
				);

				if (widthChanged) {
					this.objectEditorContentX = finalEvent.clientX;
				}
				if (heightChanged) {
					this.objectEditorContentY = finalEvent.clientY;
				}
			}
		} else if (
			this.objectRotateMouseDown
			&& this.selectedObjectForEdition
			&& !Array.isArray(finalEvent)
		) {
			event.preventDefault();
			const angleRadians = (
				Math.atan2(
					finalEvent.clientY - this.objectEditorContentRotateCenterY,
					finalEvent.clientX - this.objectEditorContentRotateCenterX,
				)
				- Math.atan2(
					this.objectEditorContentY - this.objectEditorContentRotateCenterY,
					this.objectEditorContentX - this.objectEditorContentRotateCenterX,
				)
			);
			const angleDegrees = angleRadians * (180 / Math.PI);

			let newRotate = this.selectedObjectForEdition.rotate + angleDegrees;

			if (newRotate > 360) {
				newRotate -= 360;
			} else if (newRotate < 0) {
				newRotate += 360;
			}

			const partialObject: OptionalExceptFor<EditorInteractivePageObjectModel, 'id'> = {
				id: this.selectedObjectForEdition.id,
			};

			if (this.selectedObjectForEdition.rotate !== newRotate) {
				partialObject.rotate = newRotate;
				this.selectedObjectForEdition.rotate = partialObject.rotate;
				const objectsDifferences: Record<EditorInteractivePageObjectModel['id'], Array<keyof EditorInteractivePageObjectModel>> = {
					[partialObject.id]: ['rotate'],
				};
				this.$emit(
					'change',
					this.internalPageObjects,
					objectsDifferences,
				);
				this.objectEditorContentX = finalEvent.clientX;
				this.objectEditorContentY = finalEvent.clientY;
			}
		} else if (
			this.selectedObjectForEdition
			&& Array.isArray(finalEvent)
		) {
			event.preventDefault();
			const xDifference = finalEvent[1].clientX - finalEvent[0].clientX;
			const yDifference = finalEvent[1].clientY - finalEvent[0].clientY;
			const pinchDifference = Math.hypot(
				xDifference,
				yDifference,
			);
			let difference = Math.max(
				xDifference,
				yDifference,
			);

			const previousPinchDifference = Math.hypot(
				this.objectEditorContentX || 0,
				this.objectEditorContentY || 0,
			);

			if (pinchDifference > previousPinchDifference) {
				/**
				 * Must be making it bigger from the center of the image, nothing to do
				 */
			} else if (pinchDifference < previousPinchDifference) {
				/**
				 * Must be making it smaller from the center of the image
				 */
				difference = -difference;
			}

			if (!this.isMobile) {
				difference *= this.scaling;
			}

			const objectSizeRatio = this.selectedObjectForEdition.width / this.selectedObjectForEdition.height;
			let newWidth = this.selectedObjectForEdition.width + difference;
			let newHeight = newWidth / objectSizeRatio;

			if (newWidth > this.pageModel.width) {
				newWidth = this.pageModel.width;
				newHeight = newWidth / objectSizeRatio;
			} else if (newHeight > this.pageModel.height) {
				newHeight = this.pageModel.height;
				newWidth = newHeight * objectSizeRatio;
			} else if (newWidth < 0) {
				newWidth = 0;
				newHeight = 0;
			} else if (newHeight < 0) {
				newHeight = 0;
				newWidth = 0;
			}

			const partialObject: OptionalExceptFor<EditorInteractivePageObjectModel, 'id'> = {
				id: this.selectedObjectForEdition.id,
			};
			let widthChanged = false;
			let heightChanged = false;

			if (this.selectedObjectForEdition.width !== newWidth) {
				widthChanged = true;
				partialObject.width = newWidth;
			}
			if (this.selectedObjectForEdition.height !== newHeight) {
				heightChanged = true;
				partialObject.height = newHeight;
			}

			if (typeof partialObject.width !== 'undefined') {
				if (
					partialObject.width < minSizeLimit
					&& this.selectedObjectForEdition.width > minSizeLimit
				) {
					partialObject.width = minSizeLimit;
					partialObject.height = minSizeLimit / objectSizeRatio;
				} else if (partialObject.width < minSizeLimit) {
					widthChanged = false;
					heightChanged = false;
					delete partialObject.width;
					delete partialObject.height;
					delete partialObject.x_axis;
					delete partialObject.y_axis;
				}
			}
			if (typeof partialObject.height !== 'undefined') {
				if (
					partialObject.height < minSizeLimit
					&& this.selectedObjectForEdition.height > minSizeLimit
				) {
					partialObject.height = minSizeLimit;
					partialObject.width = minSizeLimit * objectSizeRatio;
				} else if (partialObject.height < minSizeLimit) {
					heightChanged = false;
					widthChanged = false;
					delete partialObject.height;
					delete partialObject.width;
					delete partialObject.x_axis;
					delete partialObject.y_axis;
				}
			}

			if (
				widthChanged
				|| heightChanged
			) {
				if (partialObject.width) {
					const widthDifference = partialObject.width - this.selectedObjectForEdition.width;
					partialObject.x_axis = this.selectedObjectForEdition.x_axis - (widthDifference / 2);

					if (partialObject.x_axis < 0) {
						partialObject.x_axis = 0;
					} else if ((partialObject.x_axis + partialObject.width) > this.pageModel.width) {
						partialObject.x_axis = this.pageModel.width - partialObject.width;
					}
				}
				if (partialObject.height) {
					const heightDifference = partialObject.height - this.selectedObjectForEdition.height;
					partialObject.y_axis = this.selectedObjectForEdition.y_axis - (heightDifference / 2);

					if (partialObject.y_axis < 0) {
						partialObject.y_axis = 0;
					} else if ((partialObject.y_axis + partialObject.height) > this.pageModel.height) {
						partialObject.y_axis = this.pageModel.height - partialObject.height;
					}
				}

				const objectsDifferences: Record<EditorInteractivePageObjectModel['id'], Array<keyof EditorInteractivePageObjectModel>> = {
					[partialObject.id]: [],
				};
				const objectDifferences = Object.keys(partialObject) as (keyof EditorInteractivePageObjectModel)[];

				// eslint-disable-next-line no-restricted-syntax
				for (const objectDifference of objectDifferences) {
					if (objectDifference !== 'id') {
						objectsDifferences[partialObject.id].push(objectDifference);
						this.selectedObjectForEdition[objectDifference] = partialObject[objectDifference];
					}
				}

				this.$emit(
					'change',
					this.internalPageObjects,
					objectsDifferences,
				);

				this.objectEditorContentX = finalEvent[1].clientX - finalEvent[0].clientX;
				this.objectEditorContentY = finalEvent[1].clientY - finalEvent[0].clientY;
			}
		}
	}

	private onWindowMouseUp(): void {
		if (
			this.objectContentMouseDown
			|| this.objectResizeMouseDown
			|| this.objectResizeWidthMouseDown
			|| this.objectRotateMouseDown
		) {
			this.isPinch = false;
			this.objectContentMouseDown = false;
			this.objectResizeMouseDown = false;
			this.objectResizeWidthMouseDown = false;
			this.objectRotateMouseDown = false;
			this.objectEditorContentRotateCenterX = 0;
			this.objectEditorContentRotateCenterY = 0;
			this.objectEditorContentX = 0;
			this.objectEditorContentY = 0;
			this.objectResizeSide = undefined;
			document.body.style.cursor = '';

			window.removeEventListener(
				'mousemove',
				this.onWindowMouseMove,
			);
			window.removeEventListener(
				'touchmove',
				this.onWindowMouseMove,
			);
			window.removeEventListener(
				'mouseup',
				this.onWindowMouseUp,
			);
			window.removeEventListener(
				'touchend',
				this.onWindowMouseUp,
			);
			this.$emit('push-changes');
		}
	}

	private openEditorTextOptionsTooltip(
		editObjectElement: HTMLElement,
		objectModel: EditorInteractivePageObjectModel,
	): void {
		if (!this.isMobile) {
			this.destroyEditorTextOptionsTooltips(objectModel.id);

			if (!this.editorTextOptionsTooltips[objectModel.id]) {
				this.editorTextOptionsTooltips[objectModel.id] = this.$openTooltip({
					anchor: editObjectElement,
					beforeClose: (event: ServiceEvent<any>) => {
						if (
							(
								this.selectedTextObjectModel
								|| this.selectedObjectForEdition?.type === 'text'
							)
							&& !this.isMobile
						) {
							event.preventDefault();
						}
					},
					body: {
						component: EditorTextOptionsView,
						props: {
							canvas: this.computedCanvas,
							fontModels: this.fontModels,
							objectModel,
							offeringModel: this.offeringModel,
						},
						listeners: {
							change: this.onEditorTextOptionsChange,
							'eye-dropper-end': this.onEyeDropperEnd,
							'eye-dropper-start': this.onEyeDropperStart,
						},
					},
					hasCloseButton: false,
					distance: 24,
					isModal: false,
					listeners: {
						close: () => {
							delete this.editorTextOptionsTooltips[objectModel.id];
						},
					},
					theme: this.internalTheme,
				});
			}
		} else {
			this.$nextTick(() => {
				this.computedTextOptionsAnchor?.append(this.editorTextOptionsViewComponent.$el);
			});
		}
	}

	protected onEditorTextOptionsActiveModeChange(activeMode: EditorTextOptionsActiveMode | null): void {
		this.$emit(
			'editor-text-options-active-mode-change',
			activeMode,
		);
	}

	private async onEditorTextOptionsChange(
		newTextObject: EditorInteractivePageObjectModel,
		objectsDifferences: Record<EditorInteractivePageObjectModel['id'], Array<keyof EditorInteractivePageObjectModel>>,
	): Promise<void> {
		const { selectedTextObjectModel } = this;

		if (selectedTextObjectModel) {
			let objectDifferences = objectsDifferences[selectedTextObjectModel.id];

			if (objectDifferences.length) {
				const partialObjectToChange = objectDifferences.reduce(
					(partialObject, keyDifference) => {
						partialObject[keyDifference] = newTextObject[keyDifference];

						return partialObject;
					},
					{
						id: selectedTextObjectModel.id,
					} as OptionalExceptFor<EditorInteractivePageObjectModel, 'id'>,
				);
				let updatedTextObjectResult: PageObjectModelChangeResult | undefined;

				if (partialObjectToChange.fontface) {
					const newFontModel = FontModule.getById(partialObjectToChange.fontface) as FontModel;

					updatedTextObjectResult = await FontModule
						.loadModel(partialObjectToChange.fontface)
						.then(() => {
							if (
								selectedTextObjectModel.fontbold
								&& newFontModel.bold
							) {
								partialObjectToChange.fontbold = 1;
							} else {
								partialObjectToChange.fontbold = 0;
							}

							if (
								selectedTextObjectModel.fontitalic
								&& newFontModel.italic
							) {
								partialObjectToChange.fontitalic = 1;
							} else {
								partialObjectToChange.fontitalic = 0;
							}

							return ProductStateModule.changePageObject({
								...partialObjectToChange,
								noStore: true,
								originalPageObject: selectedTextObjectModel,
							});
						});
				} else {
					updatedTextObjectResult = await ProductStateModule.changePageObject({
						...partialObjectToChange,
						noStore: true,
						originalPageObject: selectedTextObjectModel,
					});
				}

				if (updatedTextObjectResult) {
					this.internalPageObjects.splice(
						this.internalPageObjects.indexOf(selectedTextObjectModel),
						1,
						updatedTextObjectResult.updatedPageObject,
					);
					const { partialPageObject } = updatedTextObjectResult;
					objectsDifferences = {
						[partialPageObject.id]: [
							...Object.keys(partialPageObject) as Array<keyof EditorInteractivePageObjectModel>,
						],
					};
					objectDifferences = objectsDifferences[partialPageObject.id];
					objectDifferences.splice(
						objectDifferences.indexOf('id'),
						1,
					);
					this.$emit(
						'change',
						this.internalPageObjects,
						objectsDifferences,
					);
				}
			}
		}
	}

	protected onEyeDropperEnd(): void {
		this.internalIsEyeDropperShown = false;
		this.$emit(
			'eye-dropper-shown-change',
			this.internalIsEyeDropperShown,
		);
	}

	protected onEyeDropperStart(): void {
		this.internalIsEyeDropperShown = true;
		this.$emit(
			'eye-dropper-shown-change',
			this.internalIsEyeDropperShown,
		);
	}

	private removeWindowClickHandler(objectModel: EditorInteractivePageObjectModel): void {
		const handler = this.windowClickHandlers[objectModel.id];

		if (handler) {
			window.removeEventListener(
				'click',
				handler,
			);
			delete this.windowClickHandlers[objectModel.id];
		}
	}

	private updateTextObjectElementContent(caretPosition: number): void {
		if (this.selectedTextObjectModel) {
			const { selectedTextObjectModel } = this;
			const editorInteractiveViewEditObjectTextContentElement = this.$el.querySelector<HTMLSpanElement>(`.editor-interactive-view-edit-object-text-content[data-object-id="${selectedTextObjectModel.id}"]`);

			if (editorInteractiveViewEditObjectTextContentElement) {
				editorInteractiveViewEditObjectTextContentElement.innerHTML = this.interactionTextObjectModelText(selectedTextObjectModel);

				if (caretPosition < 0) {
					caretPosition += (editorInteractiveViewEditObjectTextContentElement.textContent?.length || 0) + 1;
				}

				domUtils.setCaretPosition(
					editorInteractiveViewEditObjectTextContentElement,
					caretPosition,
				);
			}
		}
	}
}
